import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Grid,
  Tabs,
  Tab,
  Chip,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  TextField,
  InputAdornment,
  Autocomplete,
  Alert,
  useTheme,
} from '@mui/material';
import {
  PlayArrow as ActivateIcon,
  Stop as CloseIcon,
  Archive as ArchiveIcon,
  PersonAdd as AddPersonIcon,
  Send as ResendIcon,
  Dashboard as DashboardIcon,
  Search as SearchIcon,
  Delete as DeleteIcon,
  ArrowBack as BackIcon,
  Psychology as PsychologyIcon,
  Event as EventIcon,
  EventBusy as DeadlineIcon,
  Groups as GroupsIcon,
  Info as InfoIcon,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import {
  PageShell,
  DataTable,
  IdentityBanner,
  IdentifierChip,
  SectionCard,
  InfoItem,
  StatCard,
  PrerequisiteBlock,
  ActionButton,
  type DataTableColumn,
} from '../../components';
import psychosocialService from '../../services/psychosocialService';
import departmentService from '../../services/departmentService';
import occupationService, { Occupation } from '../../services/occupationService';
import {
  PsychosocialCampaign,
  PsychosocialCampaignEmployee,
  PsychosocialEmployeeStatus,
  EmployeeSearchResult,
} from '../../types/psychosocial';
import { Department } from '../../types/department';
import { useAcao } from '../../authorization/useVeredito';

// Espelha MIN_CAMPAIGN_RECIPIENTS do backend (PsychosocialCampaignService.ts): abaixo disso, uma
// resposta é identificável por eliminação mesmo sem visão individual do dashboard.
const MIN_CAMPAIGN_RECIPIENTS = 5;

const employeeStatusLabels: Record<PsychosocialEmployeeStatus, string> = {
  pending: 'Pendente',
  notified: 'Notificado',
  in_progress: 'Em Progresso',
  completed: 'Completado',
  expired: 'Expirado',
};

const employeeStatusColors: Record<PsychosocialEmployeeStatus, 'default' | 'primary' | 'success' | 'warning' | 'error' | 'info'> = {
  pending: 'default',
  notified: 'info',
  in_progress: 'warning',
  completed: 'success',
  expired: 'error',
};

const getResponseError = (err: unknown, fallback: string): string => {
  const responseError = (err as { response?: { data?: { error?: unknown } } })?.response?.data?.error;
  return typeof responseError === 'string' ? responseError : fallback;
};

const employeeColumns: DataTableColumn<PsychosocialCampaignEmployee>[] = [
  { id: 'employee', label: 'Funcionário', primary: true, render: (ce) => ce.employee?.name || '-' },
  { id: 'email', label: 'E-mail', render: (ce) => ce.employee?.email || '-' },
  { id: 'department', label: 'Setor', render: (ce) => ce.employee?.department?.name || '-' },
  { id: 'occupation', label: 'Cargo', render: (ce) => ce.employee?.occupation?.name || '-' },
  {
    id: 'status',
    label: 'Status',
    render: (ce) => (
      <Chip size="small" label={employeeStatusLabels[ce.status]} color={employeeStatusColors[ce.status]} />
    ),
  },
  {
    id: 'notifiedAt',
    label: 'Notificado em',
    render: (ce) => (ce.notifiedAt ? new Date(ce.notifiedAt).toLocaleDateString('pt-BR') : '-'),
  },
  {
    id: 'completedAt',
    label: 'Concluído em',
    render: (ce) => (ce.completedAt ? new Date(ce.completedAt).toLocaleDateString('pt-BR') : '-'),
  },
];

const PsychosocialCampaignDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();
  const reenviar = useAcao('psychosocial.update', 'Reenviar');

  const [campaign, setCampaign] = useState<PsychosocialCampaign | null>(null);
  const [employees, setEmployees] = useState<PsychosocialCampaignEmployee[]>([]);
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [totalCount, setTotalCount] = useState(0);
  // Contagem sem filtro: `totalCount` reflete a busca (`search`) e zera também quando o termo
  // digitado não casa com ninguém — não serve para decidir se a campanha está vazia. `null`
  // até a primeira resposta chegar, para não piscar o bloqueio antes da contagem real.
  const [totalUnfiltered, setTotalUnfiltered] = useState<number | null>(null);
  const [search, setSearch] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState<{ open: boolean; title: string; text: string; action: () => void }>({ open: false, title: '', text: '', action: () => {} });
  const [addEmployeesOpen, setAddEmployeesOpen] = useState(false);
  const [addTab, setAddTab] = useState(0);
  const [addLoading, setAddLoading] = useState(false);
  // Department tab
  const [departments, setDepartments] = useState<Department[]>([]);
  const [selectedDepartments, setSelectedDepartments] = useState<Department[]>([]);
  // Occupation tab
  const [occupations, setOccupations] = useState<Occupation[]>([]);
  const [selectedOccupations, setSelectedOccupations] = useState<Occupation[]>([]);
  // Individual tab
  const [employeeSearchQuery, setEmployeeSearchQuery] = useState('');
  const [employeeSearchResults, setEmployeeSearchResults] = useState<EmployeeSearchResult[]>([]);
  const [selectedEmployees, setSelectedEmployees] = useState<EmployeeSearchResult[]>([]);
  const [employeeSearchLoading, setEmployeeSearchLoading] = useState(false);
  const searchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Mantém a busca fora das dependências: ela só dispara no Enter.
  const searchRef = useRef(search);
  searchRef.current = search;

  const loadCampaign = useCallback(async () => {
    setLoading(true);
    try {
      const data = await psychosocialService.getCampaign(Number(id));
      setCampaign(data);
    } catch {
      enqueueSnackbar('Erro ao carregar campanha', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  }, [id, enqueueSnackbar]);

  const loadEmployees = useCallback(async () => {
    try {
      const result = await psychosocialService.getCampaignEmployees(Number(id), {
        page: page + 1,
        limit: rowsPerPage,
        search: searchRef.current || undefined,
      });
      setEmployees(result.data);
      setTotalCount(result.pagination?.total || 0);
    } catch { /* silently ignore - campaign page handles errors globally */ }
  }, [id, page, rowsPerPage]);

  useEffect(() => {
    if (id) loadCampaign();
  }, [id, loadCampaign]);

  useEffect(() => {
    if (id) loadEmployees();
  }, [id, loadEmployees]);

  const loadTotalUnfiltered = useCallback(async () => {
    try {
      const result = await psychosocialService.getCampaignEmployees(Number(id), { page: 1, limit: 1 });
      setTotalUnfiltered(result.pagination?.total ?? 0);
    } catch { /* silently ignore - campaign page handles errors globally */ }
  }, [id]);

  useEffect(() => {
    if (id) loadTotalUnfiltered();
  }, [id, loadTotalUnfiltered]);

  const handleAction = (title: string, text: string, action: () => Promise<void>) => {
    setConfirmDialog({
      open: true,
      title,
      text,
      action: async () => {
        setActionLoading(true);
        try {
          await action();
          await loadCampaign();
          await loadEmployees();
          setConfirmDialog((prev) => ({ ...prev, open: false }));
        } catch (err: unknown) {
          enqueueSnackbar(getResponseError(err, 'Erro na operação'), { variant: 'error' });
        } finally {
          setActionLoading(false);
        }
      },
    });
  };

  const handleActivate = () => handleAction(
    'Ativar Campanha',
    'Ao ativar, tokens serão gerados e notificações enviadas a todos os funcionários. Deseja continuar?',
    async () => {
      const result = await psychosocialService.activateCampaign(Number(id));
      enqueueSnackbar(`Campanha ativada! ${result.totalNotified} funcionários notificados.`, { variant: 'success' });
    },
  );

  const handleClose = () => handleAction(
    'Fechar Campanha',
    'Ao fechar, funcionários pendentes não poderão mais responder. Deseja continuar?',
    async () => {
      await psychosocialService.closeCampaign(Number(id));
      enqueueSnackbar('Campanha fechada com sucesso', { variant: 'success' });
    },
  );

  const handleArchive = () => handleAction(
    'Arquivar Campanha',
    'Deseja arquivar esta campanha?',
    async () => {
      await psychosocialService.archiveCampaign(Number(id));
      enqueueSnackbar('Campanha arquivada com sucesso', { variant: 'success' });
    },
  );

  const handleResend = async (empId: number) => {
    try {
      await psychosocialService.resendNotification(Number(id), empId);
      enqueueSnackbar('Notificação reenviada', { variant: 'success' });
      loadEmployees();
    } catch (err: unknown) {
      enqueueSnackbar(getResponseError(err, 'Erro ao reenviar'), { variant: 'error' });
    }
  };

  const loadDepartmentsAndOccupations = useCallback(async () => {
    if (!campaign?.companyId) return;
    try {
      const [depts, occs] = await Promise.all([
        departmentService.getDepartmentsByCompanyId(String(campaign.companyId)),
        occupationService.getOccupations({ limit: 500, filters: { companyId: campaign.companyId } }),
      ]);
      setDepartments(depts);
      setOccupations(occs.data);
    } catch { /* ignore */ }
  }, [campaign?.companyId]);

  const handleOpenAddDialog = () => {
    setAddEmployeesOpen(true);
    setAddTab(0);
    setSelectedDepartments([]);
    setSelectedOccupations([]);
    setSelectedEmployees([]);
    setEmployeeSearchQuery('');
    setEmployeeSearchResults([]);
    loadDepartmentsAndOccupations();
  };

  const handleEmployeeSearch = useCallback(async (query: string) => {
    if (query.length < 2) {
      setEmployeeSearchResults([]);
      return;
    }
    setEmployeeSearchLoading(true);
    try {
      const results = await psychosocialService.searchEmployeesForCampaign(Number(id), query);
      setEmployeeSearchResults(results);
    } catch { /* ignore */ } finally {
      setEmployeeSearchLoading(false);
    }
  }, [id]);

  const handleEmployeeSearchInputChange = (_event: React.SyntheticEvent, value: string) => {
    setEmployeeSearchQuery(value);
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(() => handleEmployeeSearch(value), 300);
  };

  const formatCpf = (cpf: string) => {
    if (!cpf || cpf.length !== 11) return cpf;
    return `${cpf.slice(0, 3)}.${cpf.slice(3, 6)}.${cpf.slice(6, 9)}-${cpf.slice(9)}`;
  };

  const handleAddEmployees = async (payload: Parameters<typeof psychosocialService.addCampaignEmployees>[1]) => {
    setAddLoading(true);
    try {
      const result = await psychosocialService.addCampaignEmployees(Number(id), payload);
      enqueueSnackbar(`${result.added} funcionário(s) adicionado(s)`, { variant: 'success' });
      setAddEmployeesOpen(false);
      setTotalUnfiltered(result.total);
      loadEmployees();
    } catch (err: unknown) {
      enqueueSnackbar(getResponseError(err, 'Erro ao adicionar funcionários'), { variant: 'error' });
    } finally {
      setAddLoading(false);
    }
  };

  const handleRemoveEmployee = async (employeeId: number) => {
    try {
      await psychosocialService.removeCampaignEmployee(Number(id), employeeId);
      enqueueSnackbar('Funcionário removido', { variant: 'success' });
      loadEmployees();
      loadTotalUnfiltered();
    } catch (err: unknown) {
      enqueueSnackbar(getResponseError(err, 'Erro ao remover'), { variant: 'error' });
    }
  };

  const backButton = (
    <Button variant="outlined" size="small" startIcon={<BackIcon />} onClick={() => navigate('/psychosocial')}>
      Voltar
    </Button>
  );

  if (loading || !campaign) {
    return (
      <PageShell
        breadcrumbs={[
          { label: 'Psicossocial', path: '/psychosocial' },
          { label: 'Campanhas', path: '/psychosocial' },
        ]}
        actions={backButton}
      >
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      </PageShell>
    );
  }

  const statusLabels: Record<string, string> = { draft: 'Rascunho', active: 'Ativa', closed: 'Fechada', archived: 'Arquivada' };
  const statusColors: Record<string, 'default' | 'success' | 'warning' | 'info'> = {
    draft: 'default',
    active: 'success',
    closed: 'warning',
    archived: 'info',
  };
  const fmtDate = (value: string | null) =>
    value ? new Date(value).toLocaleDateString('pt-BR') : '';
  const companyName = campaign.company?.tradeName || campaign.company?.legalName;
  const abaixoDoMinimo =
    campaign.status === 'draft' && totalUnfiltered !== null && totalUnfiltered < MIN_CAMPAIGN_RECIPIENTS;

  return (
    <PageShell
      breadcrumbs={[
        { label: 'Psicossocial', path: '/psychosocial' },
        { label: 'Campanhas', path: '/psychosocial' },
        { label: campaign.name },
      ]}
      actions={
        <>
          {backButton}
          {campaign.status === 'draft' && !abaixoDoMinimo && (
            <ActionButton acao="psychosocial.update" rotulo="Ativar" variant="contained" size="small" color="success" startIcon={<ActivateIcon />} onClick={handleActivate}>
              Ativar
            </ActionButton>
          )}
          {campaign.status === 'active' && (
            <ActionButton acao="psychosocial.update" rotulo="Fechar" variant="outlined" size="small" color="warning" startIcon={<CloseIcon />} onClick={handleClose}>
              Fechar
            </ActionButton>
          )}
          {campaign.status === 'closed' && (
            <ActionButton acao="psychosocial.update" rotulo="Arquivar" variant="outlined" size="small" startIcon={<ArchiveIcon />} onClick={handleArchive}>
              Arquivar
            </ActionButton>
          )}
          {(campaign.status === 'active' || campaign.status === 'closed') && (
            <Button
              variant="outlined"
              size="small"
              startIcon={<DashboardIcon />}
              onClick={() => navigate(`/psychosocial/campaigns/${id}/dashboard`)}
            >
              Dashboard
            </Button>
          )}
        </>
      }
    >
        {/* ═══ 1. Identidade ═══ */}
        <IdentityBanner
          avatar={<PsychologyIcon sx={{ fontSize: 32 }} />}
          name={campaign.name}
          color="#8E24AA"
          status={
            <Chip
              size="small"
              label={statusLabels[campaign.status] || campaign.status}
              color={statusColors[campaign.status] || 'default'}
            />
          }
          context={`${companyName || '—'} › ${campaign.form?.name || '—'}`}
          identifiers={<IdentifierChip label={`Campanha #${campaign.id}`} />}
        />

        {abaixoDoMinimo && (
          <Box sx={{ mb: 3 }}>
            <PrerequisiteBlock
              title={
                totalUnfiltered === 0
                  ? 'Esta campanha não tem funcionários'
                  : `Esta campanha tem apenas ${totalUnfiltered} funcionário${totalUnfiltered === 1 ? '' : 's'}`
              }
              reason={`A campanha só pode ser ativada com pelo menos ${MIN_CAMPAIGN_RECIPIENTS} funcionários, para que as respostas não possam ser identificadas por eliminação.`}
              resolve={{
                label: 'Adicionar funcionários',
                permission: 'psychosocial.update',
                mode: 'modal',
                onResolve: handleOpenAddDialog,
              }}
              deniedHint="Peça a um administrador para incluir mais funcionários nesta campanha antes de ativá-la."
            />
          </Box>
        )}

        {/* ═══ 2. Destaques ═══ */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid size={{ xs: 12, sm: 4 }}>
            <StatCard
              icon={<EventIcon fontSize="small" />}
              label="Início"
              value={fmtDate(campaign.startDate) || '—'}
              color={theme.palette.info.main}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 4 }}>
            <StatCard
              icon={<DeadlineIcon fontSize="small" />}
              label="Prazo"
              value={fmtDate(campaign.endDate) || '—'}
              color={theme.palette.warning.main}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 4 }}>
            <StatCard
              icon={<GroupsIcon fontSize="small" />}
              label="Funcionários"
              value={String(totalCount)}
              color={theme.palette.success.main}
            />
          </Grid>
        </Grid>

        {/* ═══ 3. Blocos temáticos ═══ */}
        <Box sx={{ mb: 3 }}>
          <SectionCard
            title="Informações da Campanha"
            icon={<InfoIcon fontSize="small" />}
            accentColor={theme.palette.success.main}
          >
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <InfoItem label="Empresa" value={companyName} />
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <InfoItem label="Formulário" value={campaign.form?.name} />
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <InfoItem label="Início" value={fmtDate(campaign.startDate)} />
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <InfoItem label="Prazo" value={fmtDate(campaign.endDate)} />
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <InfoItem label="Criada em" value={fmtDate(campaign.createdAt)} />
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <InfoItem label="Fechada em" value={fmtDate(campaign.closedAt)} />
              </Grid>
              <Grid size={12}>
                <InfoItem label="Descrição" value={campaign.description} fullWidth />
              </Grid>
            </Grid>
          </SectionCard>
        </Box>

        {/* ═══ 4. Relacionados ═══ */}
        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
          <Tabs value={tab} onChange={(_e, v) => setTab(v)}>
            <Tab label="Funcionários" />
          </Tabs>
        </Box>

        {tab === 0 && (
          <SectionCard
            title="Funcionários"
            icon={<GroupsIcon fontSize="small" />}
            accentColor={theme.palette.success.main}
            badge={<Chip size="small" label={totalCount} />}
            action={
              campaign.status === 'draft' ? (
                <ActionButton
                  acao="psychosocial.update"
                  rotulo="Adicionar"
                  size="small"
                  variant="outlined"
                  startIcon={<AddPersonIcon />}
                  onClick={handleOpenAddDialog}
                >
                  Adicionar
                </ActionButton>
              ) : undefined
            }
          >
            <Box sx={{ mb: 2 }}>
              <TextField
                size="small"
                placeholder="Buscar..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && loadEmployees()}
                InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment> }}
                sx={{ width: 300, maxWidth: '100%' }}
              />
            </Box>

            <DataTable
              columns={employeeColumns}
              rows={employees}
              getRowId={(ce) => ce.id}
              emptyState={{
                title: 'Nenhum funcionário nesta campanha',
                description:
                  'Os funcionários entram na campanha quando você os adiciona por setor, por cargo ou um a um, e é daí que saem os convites para responder.',
              }}
              actions={[
                {
                  icon: <ResendIcon fontSize="small" />,
                  tooltip: reenviar.tooltip,
                  onClick: (ce) => handleResend(ce.employeeId),
                  hidden: (ce) => ce.status !== 'expired' && ce.status !== 'notified',
                  disabled: reenviar.disabled,
                },
                {
                  icon: <DeleteIcon fontSize="small" />,
                  tooltip: 'Remover',
                  color: 'error',
                  onClick: (ce) => handleRemoveEmployee(ce.employeeId),
                  hidden: () => campaign.status !== 'draft',
                },
              ]}
              pagination={{
                page,
                rowsPerPage,
                totalCount,
                onPageChange: setPage,
                onRowsPerPageChange: (value) => { setRowsPerPage(value); setPage(0); },
                rowsPerPageOptions: [10, 20, 50],
              }}
            />
          </SectionCard>
        )}

        <Dialog open={addEmployeesOpen} onClose={() => setAddEmployeesOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Adicionar Funcionários</DialogTitle>
          <DialogContent>
            <Tabs value={addTab} onChange={(_e, v) => setAddTab(v)} sx={{ mb: 2 }}>
              <Tab label="Todos" />
              <Tab label="Por Setor" />
              <Tab label="Por Cargo" />
              <Tab label="Individual" />
            </Tabs>

            {addTab === 0 && (
              <Box>
                <Alert severity="info" sx={{ mb: 2 }}>
                  Todos os funcionários ativos da empresa serão adicionados à campanha.
                </Alert>
                <Button
                  variant="contained"
                  fullWidth
                  disabled={addLoading}
                  onClick={() => handleAddEmployees({ addAll: true })}
                >
                  {addLoading ? 'Adicionando...' : 'Adicionar Todos'}
                </Button>
              </Box>
            )}

            {addTab === 1 && (
              <Box>
                <Autocomplete
                  multiple
                  options={departments}
                  getOptionLabel={(opt) => opt.name}
                  value={selectedDepartments}
                  onChange={(_e, val) => setSelectedDepartments(val)}
                  renderInput={(params) => <TextField {...params} label="Selecione os setores" placeholder="Buscar setor..." />}
                  sx={{ mb: 2 }}
                />
                <Button
                  variant="contained"
                  fullWidth
                  disabled={addLoading || selectedDepartments.length === 0}
                  onClick={() => handleAddEmployees({ departmentIds: selectedDepartments.map((d) => Number(d.id)) })}
                >
                  {addLoading ? 'Adicionando...' : `Adicionar por Setor (${selectedDepartments.length} selecionado${selectedDepartments.length !== 1 ? 's' : ''})`}
                </Button>
              </Box>
            )}

            {addTab === 2 && (
              <Box>
                <Autocomplete
                  multiple
                  options={occupations}
                  getOptionLabel={(opt) => opt.name}
                  value={selectedOccupations}
                  onChange={(_e, val) => setSelectedOccupations(val)}
                  renderInput={(params) => <TextField {...params} label="Selecione os cargos" placeholder="Buscar cargo..." />}
                  sx={{ mb: 2 }}
                />
                <Button
                  variant="contained"
                  fullWidth
                  disabled={addLoading || selectedOccupations.length === 0}
                  onClick={() => handleAddEmployees({ occupationIds: selectedOccupations.map((o) => o.id) })}
                >
                  {addLoading ? 'Adicionando...' : `Adicionar por Cargo (${selectedOccupations.length} selecionado${selectedOccupations.length !== 1 ? 's' : ''})`}
                </Button>
              </Box>
            )}

            {addTab === 3 && (
              <Box>
                <Autocomplete
                  multiple
                  options={employeeSearchResults}
                  getOptionLabel={(opt) => `${opt.name} - ${formatCpf(opt.cpf)}`}
                  filterOptions={(x) => x}
                  value={selectedEmployees}
                  onChange={(_e, val) => setSelectedEmployees(val)}
                  onInputChange={handleEmployeeSearchInputChange}
                  inputValue={employeeSearchQuery}
                  loading={employeeSearchLoading}
                  isOptionEqualToValue={(opt, val) => opt.id === val.id}
                  noOptionsText={employeeSearchQuery.length < 2 ? 'Digite ao menos 2 caracteres' : 'Nenhum funcionário encontrado'}
                  renderOption={(props, option) => (
                    <li {...props} key={option.id}>
                      <Box>
                        <Typography variant="body2">{option.name}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          CPF: {formatCpf(option.cpf)}
                          {option.department ? ` | ${option.department.name}` : ''}
                          {option.occupation ? ` | ${option.occupation.name}` : ''}
                        </Typography>
                      </Box>
                    </li>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Buscar por nome, CPF ou matrícula"
                      placeholder="Digite para buscar..."
                    />
                  )}
                  sx={{ mb: 2 }}
                />
                {selectedEmployees.length > 0
                  && (totalUnfiltered ?? 0) + selectedEmployees.length < MIN_CAMPAIGN_RECIPIENTS && (
                  <Alert severity="warning" sx={{ mb: 2 }}>
                    Com essa seleção a campanha ficaria com{' '}
                    {(totalUnfiltered ?? 0) + selectedEmployees.length} funcionário
                    {(totalUnfiltered ?? 0) + selectedEmployees.length === 1 ? '' : 's'}. São
                    necessários pelo menos {MIN_CAMPAIGN_RECIPIENTS} para ativar, de forma que uma
                    resposta não possa ser identificada por eliminação.
                  </Alert>
                )}
                <Button
                  variant="contained"
                  fullWidth
                  disabled={addLoading || selectedEmployees.length === 0}
                  onClick={() => handleAddEmployees({ employeeIds: selectedEmployees.map((e) => e.id) })}
                >
                  {addLoading ? 'Adicionando...' : `Adicionar Selecionados (${selectedEmployees.length})`}
                </Button>
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setAddEmployeesOpen(false)}>Fechar</Button>
          </DialogActions>
        </Dialog>

        <Dialog open={confirmDialog.open} onClose={() => setConfirmDialog((p) => ({ ...p, open: false }))}>
          <DialogTitle>{confirmDialog.title}</DialogTitle>
          <DialogContent><DialogContentText>{confirmDialog.text}</DialogContentText></DialogContent>
          <DialogActions>
            <Button onClick={() => setConfirmDialog((p) => ({ ...p, open: false }))}>Cancelar</Button>
            <Button onClick={confirmDialog.action} variant="contained" disabled={actionLoading}>
              {actionLoading ? 'Processando...' : 'Confirmar'}
            </Button>
          </DialogActions>
        </Dialog>
    </PageShell>
  );
};

export default PsychosocialCampaignDetailPage;
